/**
 * 引き継ぎコードのパネル。
 *
 * **Phaser ではなく DOM で作る。** 文字入力が要るからで、
 * キャンバスの上に自前の IME を作るのは割に合わない。
 * ゲーム本編は 1 本指で完結し、ここだけがキーボードを使う画面になる。
 */

import { t } from '../i18n';
import { formatCode, restoreIdentity } from '../platform/identity';

const BG = '#04212c';
const PANEL = '#0a4050';
const INK = '#e7f1ee';
const DIM = '#9fb9bd';
const ACCENT = '#3fd6c0';
const WARN = '#ff7a52';

let open = false;

export interface CodePanelOptions {
  /** いま持っているコード。サーバー未接続なら null。 */
  readonly code: string | null;
  /** 引き継ぎが成功して身元が変わったとき。呼び出し側は表示を作り直す。 */
  readonly onRestored: () => void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  style: Partial<CSSStyleDeclaration>,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  Object.assign(node.style, style);
  if (text !== undefined) node.textContent = text;
  return node;
}

export function openCodePanel(options: CodePanelOptions): void {
  // 二重に開かせない。開いている間はキャンバスに触らせない。
  if (open) return;
  open = true;

  const root = el('div', {
    position: 'fixed',
    inset: '0',
    zIndex: '50',
    background: `${BG}f2`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    boxSizing: 'border-box',
    font: '15px/1.7 system-ui, -apple-system, "Hiragino Sans", sans-serif',
    color: INK,
  });

  const card = el('div', {
    width: '100%',
    maxWidth: '360px',
    background: PANEL,
    borderRadius: '14px',
    padding: '24px',
    boxSizing: 'border-box',
  });

  card.append(
    el('div', { fontSize: '19px', fontWeight: '700', marginBottom: '6px' }, t().codeTitle),
    el(
      'div',
      { color: DIM, fontSize: '13px', marginBottom: '18px' },
      t().codeLead,
    ),
  );

  if (options.code) {
    card.append(
      el(
        'div',
        {
          font: '700 26px/1.4 ui-monospace, SFMono-Regular, Menlo, monospace',
          letterSpacing: '2px',
          color: ACCENT,
          textAlign: 'center',
          padding: '12px 0',
          background: `${BG}99`,
          borderRadius: '10px',
          userSelect: 'text',
          webkitUserSelect: 'text',
        } as Partial<CSSStyleDeclaration>,
        formatCode(options.code),
      ),
      el(
        'div',
        { color: DIM, fontSize: '12px', marginTop: '8px' },
        t().codeMemo,
      ),
    );
  } else {
    card.append(
      el(
        'div',
        { color: DIM, fontSize: '13px', padding: '10px 0' },
        t().codeNoServer,
      ),
    );
  }

  // ── 入力 ──
  card.append(
    el('div', { marginTop: '22px', fontSize: '14px', fontWeight: '700' }, t().codeEnterLabel),
  );

  const input = el('input', {
    width: '100%',
    boxSizing: 'border-box',
    marginTop: '8px',
    padding: '12px',
    borderRadius: '8px',
    border: `1px solid ${DIM}55`,
    background: `${BG}cc`,
    color: INK,
    font: '600 18px/1.2 ui-monospace, SFMono-Regular, Menlo, monospace',
    letterSpacing: '2px',
    textAlign: 'center',
  });
  input.type = 'text';
  input.placeholder = 'XXXX-XXXX';
  input.autocapitalize = 'characters';
  input.autocomplete = 'off';
  input.spellcheck = false;
  input.maxLength = 12;
  card.append(input);

  const message = el('div', { minHeight: '20px', fontSize: '13px', marginTop: '8px' }, '');
  card.append(message);

  const buttons = el('div', { display: 'flex', gap: '10px', marginTop: '6px' });
  const restore = el(
    'button',
    {
      flex: '1',
      padding: '13px',
      borderRadius: '8px',
      border: 'none',
      background: ACCENT,
      color: BG,
      font: '700 15px system-ui, sans-serif',
      cursor: 'pointer',
    },
    t().codeSubmit,
  );
  const close = el(
    'button',
    {
      flex: '1',
      padding: '13px',
      borderRadius: '8px',
      border: 'none',
      background: `${BG}cc`,
      color: INK,
      font: '600 15px system-ui, sans-serif',
      cursor: 'pointer',
    },
    t().codeClose,
  );
  buttons.append(restore, close);
  card.append(buttons);

  const dismiss = () => {
    open = false;
    root.remove();
  };

  close.addEventListener('click', dismiss);
  // 背景を触ったら閉じる。カード自体のタップでは閉じない。
  root.addEventListener('click', (e) => {
    if (e.target === root) dismiss();
  });

  const say = (text: string, color: string) => {
    message.textContent = text;
    message.style.color = color;
  };

  const submit = async () => {
    restore.disabled = true;
    say(t().codeChecking, DIM);
    const result = await restoreIdentity(input.value);
    restore.disabled = false;
    if (result.ok) {
      // **何が起きたかを言う。** 「引き継ぎました」とだけ出して表が空のままだと、
      // 失敗したのか、元から記録が無いのかが本人に分からない。
      if (result.staleServer) {
        // 本人には直せないが、**「記録が無い」と誤解させない**ことが大事。
        say(t().codeStaleServer, WARN);
        options.onRestored();
        return;
      }
      say(
        result.restored > 0
          ? t().codeRestored(result.restored)
          : t().codeRestoredEmpty,
        ACCENT,
      );
      options.onRestored();
      setTimeout(dismiss, result.restored > 0 ? 1400 : 2600);
      return;
    }
    say(
      {
        'bad-code': t().codeBad,
        'not-found': t().codeNotFound,
        offline: t().codeOffline,
        'no-server': t().codeNoServerRestore,
      }[result.reason],
      WARN,
    );
  };

  restore.addEventListener('click', () => void submit());
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') void submit();
  });

  root.append(card);
  document.body.append(root);
}
